import { useCallback, type FocusEvent } from 'react';

import type { EvaluationParams, FinanceType, ToleranceBound } from 'types';

import { blurOnEnter } from './blurOnEnter';

type DegreeParamKey = 'toleranceDegrees' | 'minDegrees' | 'maxDegrees';

// Shared by evaluationTemplateRegistry descriptors and the EvaluationParamsEditor dispatcher; living in
// either of those files instead would close a module cycle (dispatcher -> registry -> descriptors -> these).

const round = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

// Bounds are stored as signed fractions (-0.25 = -25%); the editor shows whole percents so an
// admin types "25" rather than "0.25".
export const fractionToPercentDisplay = (fraction: number | undefined): string =>
  fraction === undefined ? '' : String(round(fraction * 100, 6));

export const percentDisplayToFraction = (display: string): number | undefined => {
  const trimmed = display.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : round(parsed / 100, 6);
};

const numberInputClassName =
  'focus:border-interactive-primary w-64 rounded-[4px] border border-border-default bg-canvas-default px-8 py-4 body-300 leading-none text-text-primary outline-none';

export interface ToleranceNumberInputProps {
  defaultValue: string | number;
  placeholder: string;
  ariaLabel: string;
  // The caller parses the raw string (percent vs. degrees); this component only wires up the
  // uncontrolled input's blur-commit and Enter-to-blur mechanics.
  onCommitValue: (raw: string) => void;
}

export const ToleranceNumberInput = ({
  defaultValue,
  placeholder,
  ariaLabel,
  onCommitValue,
}: ToleranceNumberInputProps) => {
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => onCommitValue(e.target.value),
    [onCommitValue],
  );

  return (
    <input
      type="number"
      defaultValue={defaultValue}
      className={numberInputClassName}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onBlur={handleBlur}
      onKeyDown={blurOnEnter}
    />
  );
};

interface ToleranceBoundRowProps {
  label: string;
  bound: ToleranceBound | undefined;
  onCommit: (next: ToleranceBound) => void;
}

export const ToleranceBoundRow = ({ label, bound, onCommit }: ToleranceBoundRowProps) => {
  const handleMinCommit = useCallback(
    (raw: string) => {
      onCommit({ ...bound, minPct: percentDisplayToFraction(raw) });
    },
    [bound, onCommit],
  );
  const handleMaxCommit = useCallback(
    (raw: string) => {
      onCommit({ ...bound, maxPct: percentDisplayToFraction(raw) });
    },
    [bound, onCommit],
  );

  return (
    <div className="flex items-center gap-8">
      <span className="w-56 shrink-0 text-sm text-text-secondary">{label}</span>
      <ToleranceNumberInput
        defaultValue={fractionToPercentDisplay(bound?.minPct)}
        placeholder="Min"
        ariaLabel={`${label} min percent`}
        onCommitValue={handleMinCommit}
      />
      <span className="text-xs text-text-tertiary">to</span>
      <ToleranceNumberInput
        defaultValue={fractionToPercentDisplay(bound?.maxPct)}
        placeholder="Max"
        ariaLabel={`${label} max percent`}
        onCommitValue={handleMaxCommit}
      />
      <span className="text-xs text-text-tertiary">%</span>
    </div>
  );
};

// Renders a signed-fraction bound as a whole-percent range (e.g. { minPct: -0.05, maxPct: 0.1 }
// -> "-5% to 10%"); undefined when neither side is set so callers can omit it.
export const formatToleranceBound = (bound: ToleranceBound | undefined): string | undefined => {
  if (!bound) return undefined;
  const min = fractionToPercentDisplay(bound.minPct);
  const max = fractionToPercentDisplay(bound.maxPct);
  if (min === '' && max === '') return undefined;
  if (min !== '' && max !== '') return `${min}% to ${max}%`;
  if (min !== '') return `≥ ${min}%`;
  return `≤ ${max}%`;
};

export const ToleranceText = ({ label, value }: { label: string; value: string }) => (
  <span className="body-300 text-text-secondary">
    {label}: {value}
  </span>
);

// Merges a finance-type-keyed tolerance bound into an EvaluationParams, preserving sibling
// finance-type bounds. Shared by evaluationTemplateRegistry descriptors whose params editor
// commits a bound under a specific FinanceType (or 'default') key.
export const mergeToleranceBound = (
  params: EvaluationParams,
  financeKey: FinanceType | 'default',
  nextBound: ToleranceBound,
): EvaluationParams => ({
  ...params,
  tolerancesByFinanceType: { ...params.tolerancesByFinanceType, [financeKey]: nextBound },
});

export const mergeDegreeParam = (params: EvaluationParams, key: DegreeParamKey, raw: string): EvaluationParams => {
  const trimmed = raw.trim();
  const parsed = trimmed === '' ? undefined : Number(trimmed);
  const value = parsed === undefined || Number.isNaN(parsed) ? undefined : parsed;

  return { ...params, [key]: value };
};
